/**
 * @TODO prototype warning, will be refactored
 * 
 * Scenario utilities for SatSimJS.
 *
 * This module provides helpers to apply a scenario configuration to a
 * running SatSim Universe + Viewer pair. It centralizes creation of objects
 * (observatories, satellites), application of simulation parameters, and
 * scheduling of simple time-based events.
 *
 * None of these utilities mutate global state; callers pass in their Universe
 * and Viewer instances explicitly. Functions are structured to be usable from
 * browser UIs, notebooks, and apps embedding SatSimJS.
 */

import {
  CallbackProperty,
  Cartesian3,
  Color,
  defined,
  JulianDate,
  Matrix3,
  Matrix4,
  Quaternion,
  Transforms
} from 'cesium'
import {
  normalizeAxisSlewRates,
  normalizeObservatoryFsmConfig,
  normalizeSensorZoomConfig
} from '../engine/objects/observatoryUtils.js'
import { validateScheduledCommandShape } from '../engine/command/schemaValidator.js'
import { booleanOr, numberOr, numberOrUndefined, toCartesian3 } from '../engine/utils.js'
import {
  createClockContext,
  isScenarioNowLiteral,
  resolveClockRangeValue,
  resolveClockStepValue,
  resolveScenarioClockTarget,
  resolveScenarioDurationSeconds,
  resolveScenarioJulianDateInput,
  resolveScenarioViewerTarget
} from './utils.js'

const DEFAULT_MODEL_MINIMUM_PIXEL_SIZE = 64
const DEFAULT_MODEL_MAXIMUM_SCALE = 20000

const _scratchEnuToFixedTransform = new Matrix4()
const _scratchEnuToFixedRotation = new Matrix3()
const _scratchBodyToEnuRotation = new Matrix3()
const _scratchBodyOffsetRotation = new Matrix3()
const _scratchRotX = new Matrix3()
const _scratchRotY = new Matrix3()
const _scratchBodyToFixedRotation = new Matrix3()
export { createClockContext } from './utils.js'

function resolveScenarioColor(input) {
  if (Array.isArray(input) && input.length === 3) {
    const components = input.map((value) => Number(value))
    if (components.every((value) => Number.isFinite(value))) {
      const useByteScale = components.some((value) => Math.abs(value) > 1)
      const rgbBytes = components
        .map((value) => (useByteScale ? value : value * 255))
        .map((value) => Math.max(0, Math.min(255, Math.round(value))))
      return Color.fromBytes(rgbBytes[0], rgbBytes[1], rgbBytes[2], 255)
    }
  }

  if (typeof input === 'string') {
    const trimmed = input.trim()
    if (!trimmed || trimmed.toLowerCase() === 'random') {
      return Color.fromRandom({ alpha: 1.0 })
    }

    const cssColor = Color.fromCssColorString(trimmed)
    if (cssColor) {
      cssColor.alpha = 1.0
      return cssColor
    }
  }

  return Color.fromRandom({ alpha: 1.0 })
}

function resolveGimbalSlewRates(input) {
  return normalizeAxisSlewRates(input)
}

function resolveScenarioFsmConfig(input, observatoryName) {
  return normalizeObservatoryFsmConfig(input, observatoryName)
}

function resolveSensorMaxDistance(input) {
  const meters = Number(
    input.sensor_max_distance ??
    input.sensorMaxDistance ??
    input.max_sensor_distance ??
    input.maxSensorDistance ??
    input.max_distance ??
    input.maxDistance ??
    input.sensor_range ??
    input.sensorRange ??
    input.range
  )
  return (Number.isFinite(meters) && meters > 0) ? meters : undefined
}

function resolveCollisionRadius(input) {
  const radius = Number(input.collision_radius)
  return (Number.isFinite(radius) && radius > 0) ? radius : undefined
}

/**
 * Normalize a scenario observatory payload entry into the canonical runtime
 * shape expected by observatory construction.
 *
 * @param {Object} sensor
 * @returns {Object}
 */
function resolveScenarioSensorConfig(sensor) {
  const payloadType = String(sensor?.type ?? '').trim().toLowerCase()
  if (payloadType === 'laser') {
    return {
      type: 'Laser',
      ...(sensor.name != null ? { name: String(sensor.name) } : {}),
      beamDivergence: Number(sensor.beamDivergence ?? sensor.beam_divergence),
      power: Number(sensor.power),
      active: booleanOr(sensor.active, false),
      maxRange: Number(sensor.maxRange ?? sensor.max_range),
      y_fov: Number(sensor.y_fov ?? 0.01),
      x_fov: Number(sensor.x_fov ?? 0.01),
      field_of_regard: sensor.field_of_regard ?? [],
      ...(sensor.color != null ? { color: sensor.color } : {})
    }
  }

  const zoom = normalizeSensorZoomConfig(sensor.zoom)
  return {
    type: 'ElectroOpticalSensor',
    ...(sensor.name != null ? { name: String(sensor.name) } : {}),
    height: Number(sensor.height ?? sensor.sensor_height),
    width: Number(sensor.width ?? sensor.sensor_width),
    y_fov: Number(sensor.y_fov ?? 5),
    x_fov: Number(sensor.x_fov ?? 5),
    field_of_regard: sensor.field_of_regard ?? [],
    ...(sensor.color != null ? { color: sensor.color } : {}),
    ...(zoom ? { zoom } : {})
  }
}

/**
 * Normalize optional multi-payload observatory definitions from a scenario entry.
 *
 * @param {Object} obs
 * @returns {Array<Object>|undefined}
 */
function resolveScenarioObservatorySensors(obs) {
  if (!(Array.isArray(obs?.sensors) && obs.sensors.length > 0)) {
    return undefined
  }
  return obs.sensors.map((sensor) => resolveScenarioSensorConfig(sensor ?? {}))
}

function resolveVelocityNed(entry, headingDeg) {
  const vNedInput = entry.velocity_ned ?? entry.velocityNed ?? entry.velocity
  if (defined(vNedInput)) {
    return toCartesian3(vNedInput)
  }

  const vEnuInput = entry.velocity_enu ?? entry.velocityEnu
  if (defined(vEnuInput)) {
    const vEnu = toCartesian3(vEnuInput)
    return new Cartesian3(vEnu.y, vEnu.x, -vEnu.z)
  }

  const speed = numberOr(entry.speed ?? entry.horizontal_speed ?? entry.ground_speed)
  const headingRad = numberOr(headingDeg) * Math.PI / 180
  const verticalSpeed = numberOr(entry.vertical_speed ?? entry.climb_rate)
  const vn = speed * Math.cos(headingRad)
  const ve = speed * Math.sin(headingRad)
  const vd = -verticalSpeed
  return new Cartesian3(vn, ve, vd)
}

function resolveAccelerationNed(entry) {
  const aNedInput = entry.acceleration_ned ?? entry.accelerationNed ?? entry.acceleration
  if (defined(aNedInput)) {
    return toCartesian3(aNedInput)
  }

  const aEnuInput = entry.acceleration_enu ?? entry.accelerationEnu
  if (defined(aEnuInput)) {
    const aEnu = toCartesian3(aEnuInput)
    return new Cartesian3(aEnu.y, aEnu.x, -aEnu.z)
  }

  return new Cartesian3()
}

function resolveScenarioWaypoint(waypoint) {
  return {
    latitude: numberOrUndefined(waypoint?.latitude),
    longitude: numberOrUndefined(waypoint?.longitude),
    altitude: numberOrUndefined(waypoint?.altitude) ?? 0,
    ...(waypoint?.time != null ? { time: waypoint.time } : {}),
    ...(waypoint?.offset != null
      ? { offsetSec: numberOrUndefined(waypoint?.offset) }
      : {}),
    ...(waypoint?.speed != null
      ? { speedMps: numberOrUndefined(waypoint?.speed) }
      : {})
  }
}

function resolveScenarioWaypointRoute(entry) {
  const routeInput = (defined(entry?.route) && typeof entry.route === 'object' && !Array.isArray(entry.route))
    ? entry.route
    : {}
  const waypointsInput = Array.isArray(routeInput.waypoints)
    ? routeInput.waypoints
    : (Array.isArray(entry?.waypoints) ? entry.waypoints : undefined)

  if (!(Array.isArray(waypointsInput) && waypointsInput.length > 0)) {
    return undefined
  }

  const route = {
    waypoints: waypointsInput.map((waypoint) => resolveScenarioWaypoint(waypoint))
  }

  const mode = routeInput.mode ?? entry?.route_mode
  if (mode != null) {
    route.mode = String(mode)
  }

  const loop = routeInput.loop ?? entry?.loop
  if (loop != null) {
    route.loop = booleanOr(loop, false)
  }

  const startTime = routeInput.start_time ?? entry?.start_time
  if (startTime != null) {
    route.startTime = startTime
  }

  const defaultSpeedMps = numberOrUndefined(
    routeInput.default_speed ??
    entry?.default_speed
  )
  if (defaultSpeedMps !== undefined) {
    route.defaultSpeedMps = defaultSpeedMps
  }

  const loopSpeedMps = numberOrUndefined(
    routeInput.loop_speed ??
    entry?.loop_speed
  )
  if (loopSpeedMps !== undefined) {
    route.loopSpeedMps = loopSpeedMps
  }

  return route
}

function formatJulianDateIso(value) {
  if (value instanceof JulianDate) {
    try {
      return JulianDate.toDate(value).toISOString()
    } catch (_) {
      return 'current'
    }
  }
  return value != null ? String(value) : 'current'
}

function isNonZeroVector3(v) {
  return defined(v) && (v.x !== 0 || v.y !== 0 || v.z !== 0)
}

function resolveScenarioModel(input) {
  const fallback = {
    modelGraphics: undefined,
    headingOffset: 0,
    pitchOffset: 0,
    rollOffset: 0,
    modelOffset: new Cartesian3()
  }
  if (!defined(input)) {
    return fallback
  }

  if (typeof input === 'string') {
    const uri = input.trim()
    if (!uri) {
      return fallback
    }
    return {
      modelGraphics: {
        uri,
        minimumPixelSize: DEFAULT_MODEL_MINIMUM_PIXEL_SIZE,
        maximumScale: DEFAULT_MODEL_MAXIMUM_SCALE
      },
      headingOffset: 0,
      pitchOffset: 0,
      rollOffset: 0,
      modelOffset: new Cartesian3()
    }
  }

  if (typeof input !== 'object') {
    return fallback
  }

  const modelGraphics = { ...input }
  const uriInput = modelGraphics.uri
  const uri = defined(uriInput) ? String(uriInput).trim() : ''
  if (!uri) {
    return fallback
  }
  modelGraphics.uri = uri

  const headingOffset = numberOr(modelGraphics.heading_offset)
  const pitchOffset = numberOr(modelGraphics.pitch_offset)
  const rollOffset = numberOr(modelGraphics.roll_offset)
  const modelOffset = toCartesian3(modelGraphics.offset)

  const minPixSize = numberOr(
    modelGraphics.min_pix_size,
    DEFAULT_MODEL_MINIMUM_PIXEL_SIZE
  )
  const maxScale = numberOr(
    modelGraphics.max_scale,
    DEFAULT_MODEL_MAXIMUM_SCALE
  )

  modelGraphics.minimumPixelSize = minPixSize
  modelGraphics.maximumScale = maxScale

  delete modelGraphics.min_pix_size
  delete modelGraphics.max_scale

  delete modelGraphics.heading_offset
  delete modelGraphics.pitch_offset
  delete modelGraphics.roll_offset
  delete modelGraphics.offset

  return { modelGraphics, headingOffset, pitchOffset, rollOffset, modelOffset }
}

function resolveScenarioModelVisualizerOptions(input) {
  const resolved = resolveScenarioModel(input)
  if (!defined(resolved.modelGraphics)) {
    return { ...resolved, visualizerModelOptions: undefined }
  }

  const visualizerModelOptions = { model: resolved.modelGraphics }
  if (isNonZeroVector3(resolved.modelOffset)) {
    visualizerModelOptions.offset = Cartesian3.clone(resolved.modelOffset)
  }
  return { ...resolved, visualizerModelOptions }
}

function createAirVehicleModelOrientationProperty(vehicle, universe, headingOffset = 0, pitchOffset = 0, rollOffset = 0) {
  return new CallbackProperty((time, result) => {
    vehicle.update(time, universe)
    const position = vehicle.position
    if (!defined(position)) {
      return Quaternion.clone(Quaternion.IDENTITY, result)
    }

    // Vehicle heading is defined clockwise from north. Build a right-handed
    // body frame in ENU where +X is forward, +Y is left, and +Z is up.
    const headingRad = numberOr(vehicle.heading + headingOffset) * Math.PI / 180
    const sinHeading = Math.sin(headingRad)
    const cosHeading = Math.cos(headingRad)
    _scratchBodyToEnuRotation[0] = sinHeading
    _scratchBodyToEnuRotation[1] = cosHeading
    _scratchBodyToEnuRotation[2] = 0
    _scratchBodyToEnuRotation[3] = -cosHeading
    _scratchBodyToEnuRotation[4] = sinHeading
    _scratchBodyToEnuRotation[5] = 0
    _scratchBodyToEnuRotation[6] = 0
    _scratchBodyToEnuRotation[7] = 0
    _scratchBodyToEnuRotation[8] = 1

    const pitchOffsetRad = numberOr(pitchOffset) * Math.PI / 180
    const rollOffsetRad = numberOr(rollOffset) * Math.PI / 180
    Matrix3.fromRotationY(pitchOffsetRad, _scratchRotY)
    Matrix3.fromRotationX(rollOffsetRad, _scratchRotX)
    Matrix3.multiply(_scratchRotY, _scratchRotX, _scratchBodyOffsetRotation)
    Matrix3.multiply(_scratchBodyToEnuRotation, _scratchBodyOffsetRotation, _scratchBodyToEnuRotation)

    const enuToFixedTransform = Transforms.eastNorthUpToFixedFrame(position, undefined, _scratchEnuToFixedTransform)
    const enuToFixedRotation = Matrix4.getMatrix3(enuToFixedTransform, _scratchEnuToFixedRotation)
    const bodyToFixedRotation = Matrix3.multiply(enuToFixedRotation, _scratchBodyToEnuRotation, _scratchBodyToFixedRotation)
    return Quaternion.fromRotationMatrix(bodyToFixedRotation, result)
  }, false)
}

/**
 * Create a ground EO observatory and attach a visualizer.
 *
 * @param {Universe} universe - The SatSim Universe instance.
 * @param {Viewer} viewer - The SatSim viewer (with addObservatoryVisualizer).
 * @param {Object} obs - Observatory config.
 * @param {string} obs.name - Unique name for the observatory.
 * @param {number|string} obs.latitude - Latitude in degrees.
 * @param {number|string} obs.longitude - Longitude in degrees.
 * @param {number|string} [obs.altitude=0] - Altitude in meters.
 * @param {number|string} [obs.height=100] - Sensor height (m).
 * @param {number|string} [obs.width=100] - Sensor width (m).
 * @param {number|string} [obs.y_fov=5] - Sensor FOV in Y (deg).
 * @param {number|string} [obs.x_fov=5] - Sensor FOV in X (deg).
 * @param {Array} [obs.field_of_regard=[]] - Optional field of regard.
 * @param {Array<Object>} [obs.sensors] - Optional multi-payload definitions sharing one gimbal.
 *   EO entries may include `name`, `height`/`sensor_height`, `width`/`sensor_width`,
 *   `x_fov`, `y_fov`, `field_of_regard`, optional `zoom`, and optional `color`.
 *   Laser entries use `type: 'Laser'` plus `beam_divergence`, `power`, optional
 *   `active`, optional `max_range`, and optional placeholder `x_fov`, `y_fov`,
 *   `field_of_regard`, and `color`.
 * @param {Object} [obs.fsm] - Optional fast steering mirror config using
 *   canonical `tip`, `tilt`, and optional `slewRates`.
 * @param {Object<string, number|Object>} [obs.gimbal_slew_rates] - Optional per-axis slew settings.
 * @param {number|string} [obs.sensor_max_distance] - Optional fallback sensor range in meters when idle.
 * @param {string|Object} [obs.model] - Optional 3D model URI or Cesium model options.
 */
export function addObservatory(universe, viewer, obs) {
  if (universe.hasObject && universe.hasObject(obs.name)) {
    console.log(`Observatory with name ${obs.name} already exists, skipping creation.`)
    return
  }

  const sensors = resolveScenarioObservatorySensors(obs)
  const zoom = normalizeSensorZoomConfig(obs.zoom)
  const fsm = resolveScenarioFsmConfig(obs.fsm, obs.name)
  const o = universe.addGroundElectroOpticalObservatory({
    name: String(obs.name),
    latitude: Number(obs.latitude),
    longitude: Number(obs.longitude),
    altitude: Number(obs.altitude ?? 0),
    gimbalType: 'AzElGimbal',
    height: Number(obs.height ?? obs.sensor_height),
    width: Number(obs.width ?? obs.sensor_width),
    y_fov: Number(obs.y_fov ?? 5),
    x_fov: Number(obs.x_fov ?? 5),
    field_of_regard: obs.field_of_regard ?? [],
    ...(zoom ? { zoom } : {}),
    ...(sensors ? { sensors } : {}),
    ...(fsm ? { fsm } : {}),
    gimbalSlewRates: resolveGimbalSlewRates(
      obs.gimbal_slew_rates ??
      obs.gimbalSlewRates ??
      obs.slew_rates ??
      obs.slewRates
    ),
    sensorMaxDistance: resolveSensorMaxDistance(obs)
  })

  const desc = `<div><b>${obs.name}</b><br>` +
    `Latitude: ${obs.latitude} deg<br>` +
    `Longitude: ${obs.longitude} deg<br>` +
    `Altitude: ${obs.altitude ?? 0} m</div>`

  const { visualizerModelOptions } = resolveScenarioModelVisualizerOptions(obs.model)
  const scenarioViewer = resolveScenarioViewerTarget(viewer)
  if (scenarioViewer?.addObservatoryVisualizer) {
    scenarioViewer.addObservatoryVisualizer(o, desc, visualizerModelOptions)
  }
}

/**
 * Add a two-body satellite and attach a simple visualizer.
 *
 * Entry supports several field aliases; vectors are normalized to meters and m/s.
 *
 * @param {Universe} universe - The SatSim Universe instance.
 * @param {Viewer} viewer - The SatSim viewer (with addObjectVisualizer).
 * @param {Object} entry - Two-body definition.
 * @param {string} [entry.name] - Object name.
 * @param {Array<number>} [entry.position] - ECI position vector.
 * @param {Array<number>} [entry.velocity] - ECI velocity vector.
 * @param {Array<number>} [entry.r] - Alias for position.
 * @param {Array<number>} [entry.v] - Alias for velocity.
 * @param {Array<number>} [entry.r_km] - Position in kilometers.
 * @param {Array<number>} [entry.v_km_s] - Velocity in km/s.
 * @param {string|Date} [entry.epoch] - Epoch as ISO string or Date.
 * @param {string} [entry.orientation='nadir'] - Orientation strategy.
 * @param {string|Array<number>} [entry.color='random'] - Visualization color for the satellite.
 * @param {string|Object} [entry.model] - Optional 3D model URI or Cesium model options.
 */
export function addTwoBody(universe, viewer, entry, idx = 0) {
  const clock = resolveScenarioClockTarget(viewer) ?? createClockContext()
  const scenarioViewer = resolveScenarioViewerTarget(viewer)
  const name = entry.name || `TwoBody-${idx + 1}`
  if (universe.hasObject && universe.hasObject(name)) {
    console.log(`Satellite with name ${name} already exists, skipping creation.`)
    return
  }

  const r = entry.position
  const v = entry.velocity
  let r_m = r
  let v_m_s = v

  const R = new Cartesian3(Number(r_m[0]), Number(r_m[1]), Number(r_m[2]))
  const V = new Cartesian3(Number(v_m_s[0]), Number(v_m_s[1]), Number(v_m_s[2]))
  const t = JulianDate.fromDate(new Date(entry.epoch))
  const orientation = entry.orientation || 'nadir'

  const fallbackTime = resolveScenarioJulianDateInput(clock.currentTime, JulianDate.now())
  const s = universe.addTwoBodySatellite(name, R, V, t || fallbackTime, orientation, false, true)

  const color = resolveScenarioColor(entry.color)
  const desc = `Two-body initial state @ ${entry.epoch || 'current'}<br>` +
    `r[m]=${JSON.stringify(r_m)}<br>v[m/s]=${JSON.stringify(v_m_s)}`
  const lead = (s && s.period) ? (s.period / 2) : 1800
  const trail = (s && s.period) ? (s.period / 2) : 1800
  const res = (s && s.period && s.eccentricity !== undefined) ? (s.period / (500 / (1 - s.eccentricity))) : 60
  const { visualizerModelOptions } = resolveScenarioModelVisualizerOptions(entry.model)

  if (scenarioViewer?.addObjectVisualizer) {
    scenarioViewer.addObjectVisualizer(s, desc, {
      path: { show: false, leadTime: lead, trailTime: trail, resolution: res, material: color, width: 1 },
      point: { show: true, pixelSize: 2, color: color, outlineColor: color },
      ...(visualizerModelOptions ?? {})
    })
  }
}

/**
 * Add an air vehicle (drone/UAV) and attach a simple visualizer.
 *
 * @param {Universe} universe - The SatSim Universe instance.
 * @param {Viewer} viewer - The SatSim viewer.
 * @param {Object} entry - Air vehicle definition.
 * @param {string} [entry.name] - Object name.
 * @param {number|string} [entry.latitude] - Latitude in degrees.
 * @param {number|string} [entry.longitude] - Longitude in degrees.
 * @param {number|string} [entry.altitude=0] - Altitude in meters.
 * @param {Array<number>} [entry.velocity_ned] - Velocity [north,east,down] in m/s.
 * @param {Array<number>} [entry.acceleration_ned] - Acceleration [north,east,down] in m/s^2.
 * @param {number|string} [entry.heading] - Heading in degrees clockwise from north.
 * @param {string|Date} [entry.epoch] - Epoch as ISO string or Date.
 * @param {string|Array<number>} [entry.color='random'] - Visualization color.
 * @param {string|Object} [entry.model] - Optional 3D model URI or Cesium model options.
 */
export function addAirVehicle(universe, viewer, entry, idx = 0) {
  const clock = resolveScenarioClockTarget(viewer) ?? createClockContext()
  const scenarioViewer = resolveScenarioViewerTarget(viewer)
  const name = entry.name || `AirVehicle-${idx + 1}`
  if (universe.hasObject && universe.hasObject(name)) {
    console.log(`Air vehicle with name ${name} already exists, skipping creation.`)
    return
  }

  const waypointRoute = resolveScenarioWaypointRoute(entry)
  const scenarioStartTime = resolveScenarioJulianDateInput(clock.startTime, JulianDate.now())
  if (waypointRoute?.startTime != null && isScenarioNowLiteral(waypointRoute.startTime)) {
    waypointRoute.startTime = resolveScenarioJulianDateInput(
      waypointRoute.startTime,
      undefined,
      scenarioStartTime
    )
  }
  const initialWaypoint = waypointRoute?.waypoints?.[0]
  const latitude = numberOr(entry.latitude ?? entry.lat ?? initialWaypoint?.latitude, NaN)
  const longitude = numberOr(entry.longitude ?? entry.lon ?? entry.lng ?? initialWaypoint?.longitude, NaN)
  const altitude = numberOr(entry.altitude ?? entry.alt ?? initialWaypoint?.altitude)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    console.log(`Skipping air vehicle ${name}: invalid latitude/longitude.`)
    return
  }

  const headingInput = entry.heading ?? entry.direction
  const heading = headingInput == null ? undefined : numberOr(headingInput)
  const velocityNed = resolveVelocityNed(entry, heading ?? 0)
  const accelerationNed = resolveAccelerationNed(entry)
  const routeStart = resolveScenarioJulianDateInput(
    waypointRoute?.startTime,
    undefined,
    scenarioStartTime
  )
  const epoch = resolveScenarioJulianDateInput(
    entry.epoch,
    routeStart ?? resolveScenarioJulianDateInput(clock.currentTime, scenarioStartTime, scenarioStartTime),
    scenarioStartTime
  )
  const {
    modelGraphics,
    headingOffset,
    pitchOffset,
    rollOffset,
    visualizerModelOptions
  } = resolveScenarioModelVisualizerOptions(entry.model)

  const v = universe.addAirVehicle(
    name,
    latitude,
    longitude,
    altitude,
    velocityNed,
    accelerationNed,
    heading,
    epoch,
    true
  )

  const collisionRadius = resolveCollisionRadius(entry)
  if (Number.isFinite(collisionRadius)) {
    v.collisionRadius = collisionRadius
  }

  if (waypointRoute) {
    try {
      v.setWaypointRoute(waypointRoute, epoch)
    } catch (err) {
      if (typeof universe.removeObject === 'function') {
        universe.removeObject(v)
      }
      console.log(`Skipping air vehicle ${name}: invalid waypoint route.`, err)
      return
    }
  }

  const color = resolveScenarioColor(entry.color)
  const headingDisplay = numberOr(heading, defined(v) ? v.heading : 0)
  const routeInfo = v.waypointRoute
  const desc = routeInfo
    ? `Air vehicle waypoint route @ ${formatJulianDateIso(routeInfo.startTime)}<br>` +
      `lat=${latitude.toFixed(6)} deg, lon=${longitude.toFixed(6)} deg, alt=${altitude.toFixed(1)} m<br>` +
      `mode=${routeInfo.mode}<br>` +
      `waypoints=${routeInfo.waypoints.length}<br>` +
      `heading=${headingDisplay.toFixed(2)} deg`
    : `Air vehicle initial state @ ${entry.epoch || 'current'}<br>` +
      `lat=${latitude.toFixed(6)} deg, lon=${longitude.toFixed(6)} deg, alt=${altitude.toFixed(1)} m<br>` +
      `v_ned[m/s]=${JSON.stringify([velocityNed.x, velocityNed.y, velocityNed.z])}<br>` +
      `a_ned[m/s^2]=${JSON.stringify([accelerationNed.x, accelerationNed.y, accelerationNed.z])}<br>` +
      `heading=${headingDisplay.toFixed(2)} deg`

  const visualizerOptions = {
    path: { show: false, leadTime: 600, trailTime: 600, resolution: 5, material: color, width: 1 },
    point: { show: true, pixelSize: 4, color: color, outlineColor: color },
    ...(visualizerModelOptions ?? {})
  }
  if (defined(modelGraphics)) {
    visualizerOptions.orientation = createAirVehicleModelOrientationProperty(v, universe, headingOffset, pitchOffset, rollOffset)
  }
  if (scenarioViewer?.addObjectVisualizer) {
    scenarioViewer.addObjectVisualizer(v, desc, visualizerOptions)
  }
}

/**
 * Add an SGP4 satellite from TLE and attach a simple visualizer.
 *
 * @param {Universe} universe - The SatSim Universe instance.
 * @param {Viewer} viewer - The SatSim viewer (with addObjectVisualizer).
 * @param {string} name - Satellite name/identifier.
 * @param {string} tle1 - Line 1.
 * @param {string} tle2 - Line 2.
 * @param {string} [orientation='nadir'] - Orientation strategy.
 * @param {string|Array<number>} [color='random'] - Visualization color for the satellite.
 * @param {string|Object} [modelInput] - Optional 3D model URI or Cesium model options.
 */
export function addSatelliteFromTLE(universe, viewer, name, tle1, tle2, orientation = 'nadir', colorInput, modelInput) {
  const s = universe.addSGP4Satellite(name, tle1, tle2, orientation || 'nadir', true)
  const color = resolveScenarioColor(colorInput)
  const lead = (s && s.period) ? (s.period / 2) : 1800
  const trail = (s && s.period) ? (s.period / 2) : 1800
  const res = (s && s.period && s.eccentricity !== undefined) ? (s.period / (500 / (1 - s.eccentricity))) : 60
  const { visualizerModelOptions } = resolveScenarioModelVisualizerOptions(modelInput)
  
  const scenarioViewer = resolveScenarioViewerTarget(viewer)
  if (scenarioViewer?.addObjectVisualizer) {
    scenarioViewer.addObjectVisualizer(s, 'TLE', {
      path: { show: false, leadTime: lead, trailTime: trail, resolution: res, material: color, width: 1 },
      point: { show: true, pixelSize: 2, color: color, outlineColor: color },
      ...(visualizerModelOptions ?? {})
    })
  }
}

/**
 * Parse TLE text into an array of entries.
 *
 * Supports both 2-line (no name line) and 3-line (name line present)
 * formats, with basic robustness to blank lines and whitespace.
 *
 * @param {string} text - Raw TLE text.
 * @param {number} [limit=Infinity] - Optional cap on entries.
 * @returns {Array<{name: string, l1: string, l2: string}>}
 */
export function parseTleCatalogText(text, limit = Infinity) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map(s => s.trim())
    .filter(Boolean)

  const list = []
  for (let i = 0; i < lines.length && list.length < limit; ) {
    const a = lines[i]
    const b = lines[i + 1] || ''
    const c = lines[i + 2] || ''
    if (/^1\s/.test(a) && /^2\s/.test(b)) {
      // 2-line format
      const name = (a.substring(2, 7) || 'SAT').trim()
      list.push({ name, l1: a, l2: b })
      i += 2
    } else if (a && /^1\s/.test(b) && /^2\s/.test(c)) {
      // 3-line format with name
      list.push({ name: a, l1: b, l2: c })
      i += 3
    } else {
      i += 1
    }
  }
  return list
}

/**
 * Add a set of TLE satellites described by an object.
 *
 * Object fields:
 * - data|text: Inline TLE catalog text.
 * - url|path: URL to fetch TLE text from when no inline data is provided.
 * - limit: Maximum satellites to add (default 500000).
 * - orientation: Optional orientation strategy (e.g., 'nadir').
 * - model: Optional 3D model URI or Cesium model options applied to each catalog object.
 *
 * @param {Universe} universe - The SatSim Universe instance.
 * @param {Viewer} viewer - The SatSim viewer.
 * @param {Object} obj - TLE catalog descriptor.
 */
export async function addTleCatalog(universe, viewer, obj) {
  const limit = Number(obj.limit ?? 500000)
  let text = obj.data || obj.text || null
  if (!text) {
    const url = obj.url || obj.path
    if (!url) return
    const res = await fetch(url)
    text = await res.text()
  }
  const list = parseTleCatalogText(text, limit)
  list.slice(0, limit).forEach((sat) => {
    addSatelliteFromTLE(universe, viewer, sat.name, sat.l1, sat.l2, obj.orientation, obj.color, obj.model)
  })
}

/**
 * Apply simulation parameters to the viewer clock.
 *
 * @param {Viewer} viewer - The SatSim viewer.
 * @param {{
 *   start_time?: string,
 *   end_time?: string,
 *   duration_sec?: number,
 *   duration_seconds?: number,
 *   time_step?: number,
 *   clock_step?: string,
 *   clock_range?: string,
 *   current_time?: string,
 *   playback_state?: string
 * }} params - Parameters.
 */
export function applySimulationParameters(viewer, params) {
  const clock = resolveScenarioClockTarget(viewer) ?? createClockContext()
  if (
    defined(params.start_time) ||
    defined(params.end_time) ||
    defined(params.current_time) ||
    defined(params.duration_sec) ||
    defined(params.duration_seconds)
  ) {
    const nowAnchor = resolveScenarioJulianDateInput(clock.startTime, clock.currentTime, clock.currentTime) ?? JulianDate.now()
    const existingStart = resolveScenarioJulianDateInput(clock.startTime, nowAnchor, nowAnchor) ?? JulianDate.clone(nowAnchor, new JulianDate())
    const existingCurrent = resolveScenarioJulianDateInput(clock.currentTime, existingStart, nowAnchor) ?? JulianDate.clone(existingStart, new JulianDate())
    const existingStop = resolveScenarioJulianDateInput(
      clock.stopTime,
      JulianDate.addSeconds(existingStart, 24 * 3600, new JulianDate()),
      nowAnchor
    )
    const start = defined(params.start_time)
      ? resolveScenarioJulianDateInput(params.start_time, existingStart, nowAnchor)
      : existingStart
    const stopDefault = JulianDate.addSeconds(start, 24 * 3600, new JulianDate())
    const durationSec = resolveScenarioDurationSeconds(params.duration_sec ?? params.duration_seconds)
    const stop = defined(params.end_time)
      ? resolveScenarioJulianDateInput(params.end_time, stopDefault, nowAnchor)
      : (durationSec !== undefined
          ? JulianDate.addSeconds(start, durationSec, new JulianDate())
          : (defined(params.start_time) ? stopDefault : existingStop))
    const current = defined(params.current_time)
      ? resolveScenarioJulianDateInput(params.current_time, start, nowAnchor)
      : (defined(params.start_time) ? JulianDate.clone(start, new JulianDate()) : existingCurrent)
    clock.startTime = start.clone()
    clock.currentTime = current.clone()
    clock.stopTime = stop.clone()
  }
  if (defined(params.time_step)) clock.multiplier = Number(params.time_step)
  if (defined(params.clock_step)) {
    clock.clockStep = resolveClockStepValue(params.clock_step, clock.clockStep)
  }
  if (defined(params.clock_range)) {
    clock.clockRange = resolveClockRangeValue(params.clock_range, clock.clockRange)
  }
  if (defined(params.playback_state)) {
    const state = String(params.playback_state).toLowerCase()
    switch (state) {
      case 'play':
        clock.shouldAnimate = true
        break
      case 'pause':
      case 'stop':
        clock.shouldAnimate = false
        break
      default:
        clock.shouldAnimate = Boolean(params.playback_state)
        break
    }
  }
  return clock
}

/**
 * Add an object described by a scenario entry.
 *
 * Supported types: GroundEOObservatory (and aliases), SGP4Satellite, TLECatalog,
 * TwoBodySatellite (and aliases), AirVehicle (and aliases).
 *
 * @param {Universe} universe - The SatSim Universe instance.
 * @param {Viewer} viewer - The SatSim viewer.
 * @param {Object} obj - Scenario object entry.
 */
export function addScenarioObject(universe, viewer, obj) {
  if (!obj || !obj.type) {
    console.log('Skipping scenario object with no type', obj)
    return
  }
  switch (String(obj.type || '').toLowerCase()) {
    case 'groundeoobservatory':
    case 'groundeo':
    case 'observatory': {
      addObservatory(universe, viewer, {
        name: obj.name,
        latitude: obj.latitude,
        longitude: obj.longitude,
        altitude: obj.altitude,
        height: (obj.height != null ? obj.height : obj.sensor_height),
        width: (obj.width != null ? obj.width : obj.sensor_width),
        y_fov: obj.y_fov,
        x_fov: obj.x_fov,
        field_of_regard: obj.field_of_regard,
        zoom: obj.zoom,
        fsm: obj.fsm,
        sensors: obj.sensors,
        gimbal_slew_rates: (obj.gimbal_slew_rates != null ? obj.gimbal_slew_rates : obj.gimbalSlewRates),
        sensor_max_distance: (
          obj.sensor_max_distance != null
            ? obj.sensor_max_distance
            : (obj.sensorMaxDistance != null ? obj.sensorMaxDistance : obj.max_sensor_distance)
        ),
        model: obj.model,
      })
      break
    }
    case 'sgp4satellite':
    case 'sgp4': {
      addSatelliteFromTLE(universe, viewer, obj.name || String(obj.tle1 || '').trim(), obj.tle1, obj.tle2, obj.orientation, obj.color, obj.model)
      break
    }
    case 'tlecatalog':
    case 'tles':
    case 'tlelist': {
      addTleCatalog(universe, viewer, obj)
      break
    }
    case 'twobodysatellite':
    case 'twobody': {
      addTwoBody(universe, viewer, {
        name: obj.name,
        position: (obj.position != null ? obj.position : obj.initial_position),
        velocity: (obj.velocity != null ? obj.velocity : obj.initial_velocity),
        epoch: obj.epoch,
        orientation: obj.orientation,
        color: obj.color,
        model: obj.model,
      }, obj.__index)
      break
    }
    case 'airvehicle':
    case 'drone':
    case 'uav': {
      addAirVehicle(universe, viewer, {
        name: obj.name,
        latitude: (obj.latitude != null ? obj.latitude : obj.lat),
        longitude: (obj.longitude != null ? obj.longitude : (obj.lon != null ? obj.lon : obj.lng)),
        altitude: (obj.altitude != null ? obj.altitude : obj.alt),
        velocity_ned: (obj.velocity_ned != null ? obj.velocity_ned : (obj.velocityNed != null ? obj.velocityNed : obj.velocity)),
        acceleration_ned: (obj.acceleration_ned != null ? obj.acceleration_ned : (obj.accelerationNed != null ? obj.accelerationNed : obj.acceleration)),
        heading: obj.heading,
        direction: obj.direction,
        speed: obj.speed,
        vertical_speed: obj.vertical_speed,
        climb_rate: obj.climb_rate,
        collision_radius: obj.collision_radius,
        epoch: obj.epoch,
        color: obj.color,
        model: obj.model,
        route: obj.route,
        waypoints: obj.waypoints,
        route_mode: obj.route_mode,
        loop: obj.loop,
        start_time: obj.start_time,
        default_speed: obj.default_speed,
        loop_speed: obj.loop_speed,
      }, obj.__index)
      break
    }
  }
}

/**
 * Schedule simple time-based scenario events.
 *
 * Currently supports:
 * - type: 'trackObject' with {observer, target} fields
 *   Switches the observer's gimbal to rate tracking of the target at event time.
 * - type: 'stepGimbalAxes' with {observer, axes:{axisName:deltaDeg}}
 *   Steps one or more gimbal axis targets by delta degrees.
 * - type: 'setGimbalAxes' with {observer, axes:{axisName:targetDeg}}
 *   Sets one or more gimbal axis targets in degrees.
 * - type: 'setSensorZoom' with {observer, sensor?, zoomLevel}
 *   Sets a sensor zoom level where 0 is widest and 1 is narrowest.
 * - type: 'stepSensorZoom' with {observer, sensor?, deltaZoomLevel}
 *   Adjusts a sensor zoom level by a normalized delta.
 * - type: 'setDirectedEnergyActive' with {observer, device|sensor, active}
 *   Enables or disables a named laser payload without changing gimbal tracking.
 *
 * @param {Universe} universe - The SatSim Universe instance.
 * @param {Viewer} viewer - The SatSim viewer.
 * @param {Array<Object>} events - Array of events with `time` and `type`.
 */
export function scheduleScenarioEvents(universe, viewer, events) {
  const clock = resolveScenarioClockTarget(viewer) ?? createClockContext()
  if (!Array.isArray(events) || events.length === 0) return

  const scheduledEvents = events.map((ev, index) => {
    validateScheduledCommandShape(ev, {
      type: ev?.type,
      index
    })
    const t = ev.time
    let jd
    if (typeof t === 'number') {
      // relative seconds from scenario start
      jd = JulianDate.addSeconds(clock.startTime, t, new JulianDate())
    } else if (typeof t === 'string') {
      jd = JulianDate.fromDate(new Date(t))
    } else if (t && t.toString) {
      try { jd = JulianDate.fromDate(new Date(String(t))) } catch (_) { /* ignore */ }
    }
    if (!jd) return null

    const type = String(ev.type || '')
    const data = { ...ev }
    delete data.time
    delete data.type

    return { time: jd, command: { type, ...data } }
  }).filter(Boolean)

  scheduledEvents.forEach((event) => universe.scheduleEvent(event))
}

/**
 * Apply a full scenario object: simulation parameters, objects, and events.
 *
 * @param {Universe} universe - The SatSim Universe instance.
 * @param {Viewer} viewer - The SatSim viewer.
 * @param {Object} config - Parsed scenario JSON.
 */
export function loadScenario(universe, viewer, config) {
  if (config.simulationParameters) applySimulationParameters(viewer, config.simulationParameters)
  if (Array.isArray(config.objects)) config.objects.forEach((o, i) => addScenarioObject(universe, viewer, { ...o, __index: i }))
  if (Array.isArray(config.events)) scheduleScenarioEvents(universe, viewer, config.events)
}

export function loadScenarioRuntime(universe, clockContext, config) {
  const clock = resolveScenarioClockTarget(clockContext) ?? createClockContext()
  loadScenario(universe, clock, config)
  return clock
}

/**
 * Parse scenario JSON text and apply it.
 *
 * @param {Universe} universe - The SatSim Universe instance.
 * @param {Viewer} viewer - The SatSim viewer.
 * @param {string} text - Scenario JSON as a string.
 */
export function loadScenarioFromText(universe, viewer, text) {
  const cfg = JSON.parse(text || '{}')
  loadScenario(universe, viewer, cfg)
}

export function loadScenarioFromTextRuntime(universe, clockContext, text) {
  const cfg = JSON.parse(text || '{}')
  return loadScenarioRuntime(universe, clockContext, cfg)
}
